/**
 * dcent-pdp-buy.js — PDP 커머스 히어로 구매 컨트롤러 (drop-04 W0 계약 B).
 *
 * 히어로 구매 컬럼(R2)과 하단 고정 바(R3)가 같은 옵션 상태·같은 담기 동작을 써야 하는데,
 * 각자 구현하면 둘이 갈라진다. 그래서 상태와 담기를 이 모듈 하나가 소유하고,
 * 소비처는 **data 속성만 붙인 마크업**을 낸다. 소비처에 JS 를 두지 않는다.
 *
 * ── 소비처가 붙이는 data 속성 ──────────────────────────────────────────────
 *   [data-dcent-buy-context]              옵션 JSON (dcent-buy-context 스니펫이 출력)
 *     └ selected: URL `?variant=` 로 지목해 들어온 조합 { id, addon }. null 이면 첫 옵션
 *   [data-dcent-buy-select="<blockId>"]   옵션 카드. 클릭 시 선택. 선택 시 aria-checked=true
 *   [data-dcent-buy-addon="<blockId>"]    애드온 체크박스. 토글. 켜지면 aria-checked=true
 *   [data-dcent-buy-qty-dec|-inc]         수량 −/+ 버튼. 감소는 qty <= 1 이면 disabled 가 붙는다
 *   [data-dcent-buy-qty-input]            수량 직접 입력(number). 입력 중 빈칸 허용, blur 에 1 로 보정
 *   [data-dcent-buy-add]                  담기 버튼. data-phase 로 연출 단계가 반영됨
 *   [data-dcent-buy-now]                  바로 구매 버튼 (담기 후 /checkout)
 *   [data-dcent-buy-field="price|priceTotal|compare|discount|label|badge|addonDelta|buyTotal"]
 *                                         선택 옵션 값 자리
 *     └ 빈 값이면 hidden 이 붙는다. 그래서 소비처는 값이 없어도 **자리를 지우지 않는다**
 *     └ `price` 는 **단가**다. 수량을 곱한 합계는 `priceTotal` 로 따로 낸다 — 이 루프가 document
 *       전역이라 한 이름에 두 의미를 담으면 히어로와 고정 바가 서로 덮어쓴다
 *     └ `priceTotal`·`buyTotal` 두 개만 규칙이 다르다 — 히어로 큰 가격과 바로 구매 버튼 라벨이라
 *       자리를 비우면 가격이 사라진 히어로·빈 버튼이 된다. hidden 을 걸지 않고 값이 없으면 서버가
 *       찍어 둔 첫 페인트 값을 그대로 둔다. `buyTotal` 은 `data-format` 의 `%AMOUNT%` 를 금액으로
 *       치환해 채우고, 같은 값이 조상 버튼의 aria-label 로도 간다
 *   [data-dcent-buy-image]                선택 옵션 썸네일 <img>. data-double 로 2X 표기
 *   [data-dcent-ship-goal="<cents>"]      무료배송 게이지 루트. 없으면 게이지 비활성
 *     └ [data-dcent-ship-fill] 채움 바 · [data-dcent-ship-label] "$합계 / $목표"
 *       · [data-dcent-ship-text] 남은 금액 문구(data-remain 에 %AMOUNT% 자리표시, data-unlocked 에 달성 문구)
 *   [data-dcent-buy-error]                담기 실패 문구 자리 (성공 시 비워짐)
 *
 * ── 이벤트 ────────────────────────────────────────────────────────────────
 *   emit  dcent:pdp:state   { option, addon, qty, options }  — 초기 1회 + 상태 변할 때마다
 *   emit  dcent:cart:added  { items }
 *   emit  dcent:cart:error  { message }
 *
 * 담기 연출 타이밍은 DESIGN_GUIDE §16·18 과 동일하다(0 스피너 → 600 담김 → 1500 드로어 → 2600 복귀).
 */

const TIMING = { resolve: 600, drawer: 1500, reset: 2600 };

/**
 * 바로 구매를 위임한 뒤 버튼을 되돌리기까지. 이동은 카트를 만든 뒤라 몇 백 ms 걸리는데,
 * 그게 막히거나 느려도 화면이 잠긴 채로 남지 않게 하는 상한이다(성공하면 이 페이지를 떠난다).
 */
const BUY_NOW_RESET = 8000;

/** 숨긴 기본 결제 폼 안에서 탭 순서에 낄 수 있는 것들. */
const NATIVE_FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * 숨긴 폼을 훑는 시점(ms). 가속 결제 컴포넌트는 늦게 그려져 첫 훑기에 안 잡힌다.
 */
const NATIVE_TAME_AT = [0, 500, 1500, 3000, 6000];

/**
 * 값이 비어도 **자리를 비우지 않는** 필드.
 *
 * 나머지 필드는 빈 값이면 hidden 이 붙는다(정가·할인은 세일일 때만 보여야 하니까). 그런데 이 둘은
 * 히어로의 큰 가격과 바로 구매 버튼의 라벨이라, 같은 규칙을 적용하면 가격이 사라진 히어로와 글자
 * 없는 버튼이 남는다 — 값이 없다는 사실보다 그 화면이 더 큰 결함으로 보인다. 그래서 값이 없으면
 * 서버가 찍어 둔 첫 페인트 값을 건드리지 않고 그대로 둔다.
 */
const KEEP_WHEN_EMPTY = new Set(["priceTotal", "buyTotal"]);

/** 담기 실패를 조용히 넘기지 않는다 — 버튼을 되돌리고 문구를 띄운다. */
function showError(root, message) {
  root.querySelectorAll("[data-dcent-buy-error]").forEach((el) => {
    el.textContent = message;
    el.hidden = false;
  });
  document.dispatchEvent(
    new CustomEvent("dcent:cart:error", { detail: { message } }),
  );
}

function clearError(root) {
  root.querySelectorAll("[data-dcent-buy-error]").forEach((el) => {
    el.textContent = "";
    el.hidden = true;
  });
}

/** Minimog 벤더 드로어를 갱신 후 연다. 드로어가 꺼져 있으면 /cart 로 보낸다. */
function openCartDrawer() {
  const drawer = document.querySelector("m-cart-drawer");
  if (!drawer || typeof drawer.open !== "function") {
    window.location.href = "/cart";
    return;
  }
  if (typeof drawer.onCartDrawerUpdate === "function")
    drawer.onCartDrawerUpdate();
  drawer.open();
}

/**
 * 서버가 찍어 둔 금액 문자열을 **본으로 삼아** 다른 금액을 같은 표기로 찍는다.
 *
 * 왜 Intl 로 통째로 찍지 않는가 — 같은 금액이 통화·로케일마다 다른 모양으로 나와서, 수량을 한 번
 * 누르는 것만으로 표기가 갈린다. 실측한 것들:
 *   $75.00 → (Intl) $150      소수가 사라진다. 같은 자리에서 클릭 한 번에 표기가 바뀐다.
 *   zh-CN  → US$150           달러 기호가 `$` 에서 `US$` 로 바뀐다.
 *   ja/JPY → ￥15,000          전각 원. Shopify `money` 는 반각 `¥` 다.
 * 그래서 숫자 덩어리만 갈아끼우고 기호·자리표기는 서버 문자열을 그대로 물려받는다
 * (원본 `priceFmt(sample, value)` 와 같은 규약 — 본에 소수가 있으면 소수를 유지한다).
 *
 * 소수 판정은 원본의 `sample.includes('.')` 보다 좁게 잡는다 — 끝이 소수점 2자리일 때만이다.
 * `₩119,000` 의 `,000` 이나 유럽식 천 단위 점(`1.234`)을 소수로 오인하지 않기 위해서다.
 */
function formatLikeSample(sample, cents) {
  if (!sample) return null;
  const m = String(sample).match(/\d[\d.,]*/);
  if (!m) return null;
  const dec = /[.,]\d{2}$/.test(m[0]) ? 2 : 0;
  let num;
  try {
    num = new Intl.NumberFormat(document.documentElement.lang || "en", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(cents / 100);
  } catch {
    return null;
  }
  return sample.slice(0, m.index) + num + sample.slice(m.index + m[0].length);
}

/** 통화 기호 붙인 정수 표기. 게이지 문구용(소수 .00 은 떨어뜨린다). */
function formatAmount(cents, currency) {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return String(Math.round(value));
  }
}

class DcentPdpBuy {
  constructor(root, context) {
    this.root = root;
    this.currency = context.currency;
    this.options = (context.options || []).filter((o) => o.kind === "option");
    // 2026-08-12: 애드온이 "따로 담는 별개 상품"에서 "같은 상품의 다른 variant" 로 바뀌었다.
    // 그래서 addons 배열이 아니라 축 정보 하나만 들고, 켜고 끄면 **선택 variant 가 바뀐다**.
    // context.addon 이 null 이면 그 축이 없는 상품이라 체크박스를 그리지 않는다.
    this.addon = context.addon || null;
    // 2026-08-13: 초기 선택은 URL `?variant=` 로 지목해 들어온 조합이다(context.selected).
    // 목록 카드가 variant 낱장이 된 뒤로 그게 기본 진입 경로라, 여기서 첫 옵션으로 고정하면
    // 사람이 고른 것과 화면·담기 대상이 어긋난다(2X 를 눌러도 단품이 담기던 회귀).
    // 지목이 없거나 목록에 없는 값이면 종전대로 첫 옵션으로 떨어진다.
    const preselect = context.selected || null;
    this.selected =
      (preselect && this.options.find((o) => o.id === preselect.id)) ||
      this.options[0] ||
      null;
    // 애드온 축이 없는 상품은 지목이 뭐든 꺼진 상태다(체크박스 자체를 그리지 않는다).
    this.addonOn = Boolean(preselect && preselect.addon && this.addon);
    // 2026-08-14 (drop-07): 수량은 이 인스턴스가 소유한다. 히어로 마크업은 훅만 내고 상태를 갖지
    // 않으며, 고정 바에는 수량 UI 가 아예 없다(원본도 없다) — 두 곳이 각자 세면 반드시 갈린다.
    this.qty = 1;
    // 인풋을 지운 순간(빈칸)인가. 그동안에도 금액은 1개 기준으로 계산하되, 화면의 인풋만은
    // 되채우지 않는다 — 지우자마자 1 이 되살아나면 두 자리 수를 칠 수 없다. 보정은 blur 에서.
    this.qtyBlank = false;
    this.phase = "idle";
    this.timers = [];
  }

  bind() {
    this.root.addEventListener("click", (e) => this.onClick(e));
    // 수량 인풋은 눌러서가 아니라 쳐서 바뀌므로 클릭 위임으로는 못 받는다. blur 는 버블링하지
    // 않아 위임이 안 되니 같은 순간에 버블링하는 focusout 으로 받는다.
    this.root.addEventListener("input", (e) => this.onQtyInput(e));
    this.root.addEventListener("focusout", (e) => this.onQtyBlur(e));
    this.render();
    this.emitState("init");
    // 하단 고정 바는 히어로 밖(섹션 형제)에 있어 같은 root 에 걸리지 않는다 — 문서 레벨로 받는다.
    // 담기와 바로 구매를 각각 받는다. 소비처가 "담기 이벤트를 쏜 뒤 dcent:cart:added 를 보고
    // 결제로 이동" 하는 식으로 엮으면, 다른 곳에서 담아도 그 이동이 딸려 나가므로 분리해 둔다.
    document.addEventListener("dcent:pdp:request-add", () =>
      this.add({ then: "drawer" }),
    );
    document.addEventListener("dcent:pdp:request-buy-now", () =>
      this.buyNow(),
    );
    // 뒤로가기·앞으로가기로 이 페이지가 복원될 때는 무조건 idle 로 되돌린다.
    // bfcache 복원은 스크립트를 다시 실행하지 않아, 떠날 때의 버튼 상태가 그대로 살아난다.
    window.addEventListener("pageshow", () => this.setPhase("idle"));
    // this.after 를 쓰지 않는다 — 그 타이머는 setPhase("idle") 이 전부 지우는데, 위 pageshow 는
    // 최초 로드에서도 한 번 발동해 예약이 통째로 날아간다.
    NATIVE_TAME_AT.forEach((ms) =>
      window.setTimeout(() => this.tameNative(), ms),
    );
    this.bindReviewsLink();
  }

  /**
   * 별점 → 페이지 안 리뷰 영역으로 이동.
   *
   * 리뷰 섹션은 이 히어로 **밖의 다른 섹션**이라 Liquid 은 그 존재를 알 수 없다. 그래서 마크업은
   * 언제나 버튼으로 내고, 여기서 앵커를 실제로 찾았을 때만 `is-linked`(커서·hover)를 붙이고
   * 리스너를 건다 — 갈 곳이 없는 페이지에서 눌리는 것처럼 보이지 않게 하려는 것이다.
   *
   * 앵커 종류는 템플릿마다 다르다(dcent-pdp-social-reviews / dcent-component-testimonial-carousel).
   * 그래서 타입이 아니라 `[data-dcent-reviews]` 속성 하나로만 찾는다 — 리뷰 섹션이 늘어나도
   * 그 속성만 달면 여기는 그대로다.
   *
   * **리뷰 섹션이 둘인 페이지가 있다**(2026-09-08). 카드 키트 PDP 는 크리에이터 소셜 후기와
   * 고객 후기(Judge.me) 위젯을 같이 싣는데, 문서 첫 앵커만 보면 별점을 누른 사람이 고객 후기가
   * 아닌 인플루언서 카드에 떨어진다. 그래서 목적지 섹션이 스스로 `data-dcent-reviews="primary"`
   * 로 표시하고 여기서 그것을 **먼저** 찾는다. 없으면 종전대로 첫 앵커다 — 값을 안 쓰는 앵커만
   * 있는 페이지(지문인증형의 파트너 후기 등)는 그대로 산다.
   *
   * 목적지를 별점 쪽 값으로 정하지 않는 이유: 한때 별점이 자기 숫자의 출처(Judge.me 냐)를 적어
   * 보냈는데, dev 는 Judge.me 앱이 없어 그 값이 안 붙고 결국 첫 앵커로 떨어졌다. 숫자가 어디서
   * 왔든 사람이 기대하는 목적지는 고객 후기 한 곳이다.
   */
  bindReviewsLink() {
    const links = [...this.root.querySelectorAll("[data-dcent-reviews-link]")];
    if (!links.length) return;
    const target =
      document.querySelector('[data-dcent-reviews="primary"]') ||
      document.querySelector("[data-dcent-reviews]");
    if (!target) return;
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    links.forEach((el) => {
      el.classList.add("is-linked");
      el.addEventListener("click", () => {
        // 헤더가 스크롤 중 상단에 붙으므로 그만큼 비켜야 제목이 안 가린다 —
        // 그 여백은 CSS `scroll-margin-top` 이 갖고 있다(브라우저가 계산해 준다).
        target.scrollIntoView({
          behavior: smooth ? "smooth" : "auto",
          block: "start",
        });
      });
    });
  }

  onClick(e) {
    const select = e.target.closest("[data-dcent-buy-select]");
    if (select) {
      this.selectOption(select.getAttribute("data-dcent-buy-select"));
      return;
    }
    const addon = e.target.closest("[data-dcent-buy-addon]");
    if (addon) {
      if (!this.addon) return;
      this.addonOn = !this.addonOn;
      this.render();
      this.syncUrl();
      this.emitState();
      return;
    }
    // −/+ 는 "칸을 비워 둔 상태"를 먼저 끝낸다. 이 플래그를 내리는 다른 자리는 focusout 뿐인데,
    // macOS Safari·Firefox 는 버튼 클릭에 포커스를 주지 않아 그 이벤트가 나지 않는다. 그러면
    // 칸은 빈 채로 남고 금액만 오르는 어긋난 화면이 된다. 비운 상태의 현재 수량은 0 으로 친다 —
    // 원본이 컨트롤드 인풋이라 빈칸에서 `+` 가 1 이 되는 것과 같게 맞춘다(그냥 두면 2 가 된다).
    const qtyStep = e.target.closest(
      "[data-dcent-buy-qty-dec], [data-dcent-buy-qty-inc]",
    );
    if (qtyStep) {
      const base = this.qtyBlank ? 0 : this.qty;
      this.qtyBlank = false;
      const dec = qtyStep.matches("[data-dcent-buy-qty-dec]");
      this.setQty(dec ? base - 1 : base + 1);
      return;
    }
    const add = e.target.closest("[data-dcent-buy-add]");
    if (add) {
      e.preventDefault();
      this.add({ then: "drawer" });
      return;
    }
    const buyNow = e.target.closest("[data-dcent-buy-now]");
    if (buyNow) {
      e.preventDefault();
      this.buyNow();
    }
  }

  selectOption(id) {
    // 담기 요청이 도는 동안에는 옵션을 바꾸지 않는다. 전송 본문은 add() 시작 시점에 이미
    // 확정되므로 담기는 대상은 안 바뀌지만, 화면만 새 옵션으로 바뀌면 "무엇이 담겼는지"가
    // 어긋나 보인다. 장바구니에서 그 혼동은 비용이 크다.
    if (this.phase !== "idle") return;
    const found = this.options.find((o) => o.id === id);
    if (!found || found === this.selected) return;
    this.selected = found;
    this.render();
    this.syncUrl();
    this.emitState();
  }

  /**
   * 수량이 바뀌는 유일한 지점. 하한 1 로 클램프하고 화면·소비처를 같이 갱신한다.
   *
   * URL 에는 싣지 않는다 — `?variant=` 는 "무엇을 보고 있나"라 공유·새로고침에 남아야 맞지만,
   * 수량은 그 사람의 지금 장바구니 의사라 링크를 타고 따라가면 어색하다(벤더 PDP 도 안 싣는다).
   */
  setQty(n) {
    // 소수·문자·음수가 들어와도 조용히 1 로 떨어진다. 상한은 두지 않는다(재고는 담을 때 서버가 본다).
    this.qty = Math.max(1, Math.floor(Number(n) || 1));
    this.render();
    // reason="qty" — 갤러리는 이걸 보고 슬라이드를 그대로 둔다(수량은 옵션 변경이 아니다).
    this.emitState("qty");
  }

  /**
   * 수량 직접 입력.
   *
   * 입력 중 빈칸은 빈 채로 둔다 — 한 자리를 지우자마자 1 이 되살아나면 두 자리 수를 칠 수 없다.
   * 그동안에도 금액은 1개 기준으로 계산한다(원본의 `qtySafe = Math.max(1, qty)` 와 같은 자리).
   * 비운 채 칸을 떠나면 그때 1 로 보정한다 — 원본 `onBlur` 규칙 그대로다.
   */
  onQtyInput(e) {
    const input =
      e.target.closest && e.target.closest("[data-dcent-buy-qty-input]");
    if (!input) return;
    // type=number 는 "-" 나 "1e" 같은 미완성 입력도 빈 문자열로 준다 — 빈칸과 같이 다룬다.
    this.qtyBlank = input.value === "";
    this.setQty(input.value);
  }

  onQtyBlur(e) {
    const input =
      e.target.closest && e.target.closest("[data-dcent-buy-qty-input]");
    if (!input || !this.qtyBlank) return;
    // 플래그를 먼저 내려야 이어지는 render() 가 빈 칸에 1 을 다시 쓴다.
    this.qtyBlank = false;
    this.setQty(1);
  }

  /**
   * 선택을 URL `?variant=` 에 반영한다(히스토리는 쌓지 않는다 — 뒤로가기가 옵션 클릭 횟수만큼
   * 먹히면 안 된다).
   *
   * 진입 시 URL 의 variant 를 초기 선택으로 읽게 되었으므로, 바꾼 뒤 새로고침하거나 주소를
   * 공유하면 그 화면이 그대로 열려야 앞뒤가 맞는다. 갱신하지 않으면 방금 고른 것과 다른 옵션이
   * 열린다. 벤더 PDP(variant-picker.js)도 같은 이유로 URL 을 갱신한다.
   */
  syncUrl() {
    const cur = this.current();
    if (!cur || !cur.variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(cur.variantId));
    window.history.replaceState({}, "", url);
  }

  /**
   * 지금 선택된 variant 의 표시값 묶음. 애드온을 켜면 **같은 카드의 다른 variant** 를 본다
   * (예전처럼 줄을 하나 더 담는 게 아니다 — 장바구니엔 언제나 한 줄만 들어간다).
   * 애드온 조합이 없는 카드에서는 조용히 기본 variant 로 되돌아간다.
   */
  current() {
    const o = this.selected;
    if (!o) return null;
    if (this.addonOn && o.addonVariantId) {
      return {
        variantId: o.addonVariantId,
        available: o.addonAvailable,
        price: o.addonPrice,
        priceRaw: o.addonPriceRaw,
        compare: o.addonCompare,
        compareRaw: o.addonCompareRaw,
        discount: o.addonDiscount,
        label: o.label,
        badge: o.badge,
        image: o.image,
        imageDouble: o.imageDouble,
      };
    }
    return o;
  }

  /**
   * 히어로 큰 가격·바로 구매 버튼이 함께 쓰는 합계(원 단위 정수 — priceRaw 와 같은 단위).
   *
   * 애드온(리커버리 카드)은 **수량과 무관하게 언제나 1개**라는 결정 때문에, 곱하는 대상은 기본
   * variant 단가(base)뿐이고 차액(addonDelta)은 한 번만 더한다. 그래서 식이 base × qty + delta 다.
   * 애드온을 켜면 current() 가 애드온 variant 로 갈아타므로 그 차액이 이미 단가 안에 들어 있고,
   * 여기서 base 를 빼 차액만 되뽑는다(꺼져 있으면 0).
   *
   * 취소선 원가(compareTotalRaw)도 같은 식으로 곱한다 — 예전엔 원본을 따라 단가로 두었는데,
   * 그러면 수량 2 에서 "합계 / 단가 취소선" 이 되어 **할인 전 가격이 할인 후보다 싸 보인다**
   * (예: $118 옆에 취소선 $79). 표시 오류로 읽히는 자리라 QA 에서 지적됐다(MAGE-443).
   */
  buyTotalRaw() {
    const cur = this.current();
    if (!cur) return 0;
    const base = (this.selected && this.selected.priceRaw) || cur.priceRaw || 0;
    const addonDelta = (cur.priceRaw || 0) - base; // 애드온 꺼져 있으면 0
    return base * this.qty + addonDelta;
  }

  /**
   * 취소선 원가의 합계. buyTotalRaw() 와 같은 모델(base × qty + delta 한 번)을 쓴다 —
   * 애드온은 수량과 무관하게 1개라는 결정이 원가 쪽에도 그대로 적용돼야 두 금액의 비율이
   * 유지된다. 원가가 없는(세일이 아닌) variant 는 0 을 돌려 호출부가 자리를 비우게 한다.
   */
  compareTotalRaw() {
    const cur = this.current();
    if (!cur || !cur.compareRaw) return 0;
    const base =
      (this.selected && this.selected.compareRaw) || cur.compareRaw || 0;
    const addonDelta = (cur.compareRaw || 0) - base; // 애드온 꺼져 있으면 0
    return base * this.qty + addonDelta;
  }

  /**
   * 합계 금액 문자열. 히어로 큰 가격(`priceTotal`)과 바로 구매 버튼 라벨(`buyTotal`)이 **같은**
   * 이 값을 쓴다 — 한쪽만 따로 찍으면 같은 화면의 두 금액이 표기로 갈릴 수 있다.
   *
   * 합계가 곧 선택 variant 단가인 동안에는 Liquid 이 `money` 필터로 이미 찍어 둔 문자열
   * (`cur.price`)을 그대로 쓴다. Intl 로 다시 찍으면 서버가 낸 첫 페인트 라벨과 표기가 갈려
   * ($75.00 ↔ $75) 화면에 들어오자마자 값이 튄다. 합계가 단가와 갈라질 때(수량 2 이상 등)에만
   * formatAmount 로 계산해 찍는다.
   */
  buyTotalLabel() {
    const cur = this.current();
    if (!cur) return "";
    const raw = this.buyTotalRaw();
    if (raw <= 0) return "";
    if (raw === (cur.priceRaw || 0) && cur.price) return cur.price;
    // 서버가 찍은 단가 문자열을 본으로 삼는다 — Intl 로 새로 찍으면 수량을 누르는 순간
    // 소수·통화 기호가 바뀌어 같은 자리에서 표기가 갈린다(formatLikeSample 주석의 실측 참조).
    return formatLikeSample(cur.price, raw) || formatAmount(raw, this.currency);
  }

  /**
   * 취소선 원가 문자열. buyTotalLabel() 과 같은 규칙이다 — 합계가 단가와 같은 동안에는
   * 서버가 money 필터로 찍어 둔 문자열(cur.compare)을 그대로 써서 첫 페인트와 표기가
   * 갈리지 않게 하고, 수량 2 이상에서만 그 문자열을 본으로 삼아 새로 찍는다.
   */
  compareTotalLabel() {
    const cur = this.current();
    if (!cur || !cur.compare) return "";
    const raw = this.compareTotalRaw();
    if (raw <= 0) return "";
    if (raw === (cur.compareRaw || 0)) return cur.compare;
    return (
      formatLikeSample(cur.compare, raw) || formatAmount(raw, this.currency)
    );
  }

  /**
   * 담을 라인 목록 — 선택 variant 한 줄.
   *
   * ⚠ **애드온 축이 켜지면 이 단순화는 즉시 틀린다. 그때 반드시 고칠 것.**
   *
   * 애드온이 켜져 있으면 current() 는 애드온 variant 를 가리키는데, 여기에 quantity 를 그대로
   * 걸면 그 variant 가 수량만큼 담긴다 — "수량 3 + 리커버리 카드 1장" 이라는 디자인 의도와 달리
   * 카드가 3장 담긴다. 화면 합계(base × qty + delta)와 실제 결제 금액이 갈리는 사고다.
   *
   * 지금 이래도 되는 근거: 네 PDP 템플릿(양 샵 × biometric·card-package) 전부
   * `addon_option_name = ''` · `addon_on_value = ''` 라 애드온 축이 꺼져 있다. `dcent-buy-context`
   * 스니펫 규약대로 축이 하나면 애드온 값이 전부 비고 체크박스도 렌더되지 않는다 → addonDelta 는
   * 늘 0 이고 라인도 늘 한 줄이다. 조건이 사라지면 조용히 틀리는 종류라 여기 적어 둔다.
   *
   * 고칠 때: `기본 variant × (qty - 1)` + `애드온 variant × 1` 두 줄로 가른다. 그러면 장바구니에
   * 한 상품이 두 줄로 보이므로 "언제나 한 줄" 규약이 깨진다는 점도 같이 본다. 합계식
   * buyTotalRaw() 는 이미 그 모델(base × qty + delta 한 번)이라 수정할 필요가 없다.
   */
  lineItems() {
    const cur = this.current();
    if (!cur || !cur.variantId) return [];
    return [{ id: Number(cur.variantId), quantity: this.qty }];
  }

  /**
   * 바로 구매 — MAGE-492·498.
   *
   * 숨겨 둔 Shopify **기본 결제 폼**(`dcent-pdp-buy-column.liquid` 의 `[data-dcent-buy-native]`)
   * 에 지금 선택한 variant·수량을 실어 클릭을 위임한다. 기본 폼은 세션 장바구니를 건드리지
   * 않고 별도 카트를 만들어 결제로 가므로, 장바구니에 있던 라인이 따라가지 않으면서도
   * 서랍의 내용과 제휴 귀속(cart attribute 의 referral·bg_ref)이 그대로 남는다.
   *
   * ⚠️ **await 를 걸지 않는다.** 사용자 클릭 제스처 안에서 동기적으로 눌러야 브라우저가
   * 결제 이동을 사용자 행위로 인정한다. 중간에 네트워크를 기다리면 그 자격을 잃는다.
   * (종전 경로가 `/cart/add.js` 를 먼저 기다렸던 것과 다른 점이다.)
   *
   * 폴백 — 다리를 못 쓰는 상황이면 종전 경로(`/cart/add.js` → `/checkout`)로 떨어진다.
   * 스토어 설정에서 동적 결제 버튼이 꺼져 있거나 폼이 비어 렌더되는 경우가 그것이다.
   * 그때 증상(장바구니 합산)은 남지만 **구매 자체가 막히지는 않는다** — 최악이라도 지금 동작.
   */
  buyNow() {
    if (this.phase !== "idle") return; // 연출 중 재클릭 차단 — add() 와 같은 규약
    const cur = this.current();
    if (!cur || !cur.variantId) {
      showError(this.root, this.root.dataset.soldOutText || "");
      return;
    }

    const native = this.root.querySelector("[data-dcent-buy-native]");
    const idInput = native && native.querySelector("[data-dcent-buy-native-id]");
    const qtyInput =
      native && native.querySelector("[data-dcent-buy-native-qty]");
    // 동적 결제 버튼의 DOM 은 스토어·시점에 따라 갈린다(클래식 버튼 / accelerated 커스텀 엘리먼트).
    // 하나라도 잡히면 쓰고, 아무것도 없으면 폴백한다.
    const payBtn =
      native &&
      native.querySelector(
        ".shopify-payment-button__button, shopify-accelerated-checkout button, shopify-accelerated-checkout-cart button, [data-testid='Checkout-button']",
      );

    if (!idInput || !qtyInput || !payBtn) {
      // 폴백은 add() 가 스스로 잠그므로 여기서 setPhase 를 걸면 안 된다(이중 잠금).
      this.add({ then: "checkout" });
      return;
    }

    clearError(this.root);
    idInput.value = String(cur.variantId);
    qtyInput.value = String(Math.max(1, this.qtyBlank ? 1 : this.qty));
    // 위임 **직전에** 잠근다. 이 메서드는 setPhase 를 한 번도 부르지 않아 첫 줄의 재클릭
    // 가드가 늘 통과였고, 연타하면 숨은 결제 버튼이 두 번 눌려 결제 생성이 두 번 돌았다.
    // add() 와 달리 스피너도 없어 "눌린 게 안 보이니 한 번 더" 를 부추기는 상태이기도 했다.
    this.setPhase("loading", "buy");
    payBtn.click();
    // 성공하면 이 페이지를 떠나므로 이 타이머는 보통 안 터진다. 이동이 막히거나 느릴 때
    // 화면이 잠긴 채 남지 않게 하는 상한이다(결제에서 뒤로 오면 pageshow 가 또 되돌린다).
    this.after(BUY_NOW_RESET, () => this.setPhase("idle"));
  }

  /**
   * 숨긴 기본 결제 폼을 탭 순서에서 뺀다.
   *
   * 폼은 `display:none` 이 아니라 화면 밖으로 밀어 둔 것이라(숨김 방식에 따라 동적 결제
   * 버튼이 초기화되지 않는다) 키보드로는 여전히 닿는다. 래퍼에 걸어 둔 `tabindex="-1"` 은
   * **자손에 상속되지 않아** 안쪽 결제 버튼이 탭 순서에 그대로 남아 있었다 — 보이지 않는
   * 컨트롤에 포커스가 멈추고, Enter 를 치면 그대로 결제로 간다. `aria-hidden="true"` 안에
   * 포커스 가능한 요소가 있는 것 자체도 위반이라 스크린리더도 이 폼을 문제로 잡는다.
   *
   * 여러 번 훑는 이유는 가속 결제 컴포넌트가 늦게 그려져서다. 섀도 DOM 안에 버튼을 두는
   * 구현이면 여기서 못 닿는데, 그건 buyNow() 의 선택자로도 못 잡는 경우라 애초에 폴백
   * 경로(add → /checkout)로 떨어진다.
   */
  tameNative() {
    const native = this.root.querySelector("[data-dcent-buy-native]");
    if (!native) return;
    native.querySelectorAll(NATIVE_FOCUSABLE).forEach((el) => {
      el.tabIndex = -1;
    });
  }

  async add({ then }) {
    if (this.phase !== "idle") return; // 연출 중 재클릭 차단
    const items = this.lineItems();
    if (!items.length) {
      showError(this.root, this.root.dataset.soldOutText || "");
      return;
    }
    clearError(this.root);
    const group = then === "checkout" ? "buy" : "add";
    this.setPhase("loading", group);

    let ok = false;
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });
      ok = res.ok;
      if (!ok) {
        const body = await res.json().catch(() => ({}));
        showError(
          this.root,
          body.description || body.message || this.root.dataset.errorText || "",
        );
      }
    } catch {
      showError(this.root, this.root.dataset.errorText || "");
    }

    if (!ok) {
      this.setPhase("idle");
      return;
    }

    document.dispatchEvent(
      new CustomEvent("dcent:cart:added", { detail: { items } }),
    );

    if (then === "checkout") {
      // 이동 **전에** 버튼을 되돌린다. 결제 화면에서 뒤로 오면 브라우저가 이 페이지를
      // 그대로 복원(bfcache)하는데, loading 인 채로 떠나면 스피너가 영원히 도는 상태로
      // 되살아난다. 이동이 막히거나 느려도 화면이 잠기지 않는다는 이점도 있다.
      this.setPhase("idle");
      window.location.href = "/checkout";
      return;
    }

    // 응답이 빨라도 연출 순서는 유지한다 — 담김 라벨을 보여준 뒤 드로어가 열려야 이어져 보인다.
    this.after(TIMING.resolve, () => this.setPhase("added", group));
    this.after(TIMING.drawer, () => openCartDrawer());
    this.after(TIMING.reset, () => this.setPhase("idle"));
  }

  after(ms, fn) {
    this.timers.push(window.setTimeout(fn, ms));
  }

  /**
   * 진행 상태를 버튼에 반영한다.
   *
   * group = 실제로 눌린 버튼 갈래("add" | "buy"). 연출(스피너·담김 라벨)은 **누른 갈래에만**
   * 준다 — 바로 구매를 눌렀는데 장바구니 버튼이 도는 오작동을 막기 위해서다.
   * 반면 disabled 는 **양쪽 모두**에 건다. 담기 요청이 도는 동안 다른 버튼으로 한 번 더
   * 담기는 것이 이 화면에서 가장 위험한 실패라, 시각 표시보다 넓게 잠근다.
   */
  setPhase(phase, group = "add") {
    this.phase = phase;
    if (phase === "idle") {
      this.timers.forEach(window.clearTimeout);
      this.timers = [];
    }
    const busy = phase !== "idle";
    document
      .querySelectorAll("[data-dcent-buy-add], [data-dcent-buy-now]")
      .forEach((btn) => {
        const isTarget =
          group === "buy"
            ? btn.hasAttribute("data-dcent-buy-now")
            : btn.hasAttribute("data-dcent-buy-add");
        btn.setAttribute("data-phase", busy && isTarget ? phase : "idle");
        btn.toggleAttribute("disabled", busy);
      });
  }

  /** 선택 상태를 마크업에 반영. 소비처는 CSS 로만 반응한다. */
  render() {
    const sel = this.selected;
    // 표시값은 **애드온 반영 후**의 variant 에서 온다 — 체크박스를 켜면 가격이 그 자리에서 바뀐다.
    const cur = this.current();

    this.root.querySelectorAll("[data-dcent-buy-select]").forEach((el) => {
      const on = sel && el.getAttribute("data-dcent-buy-select") === sel.id;
      el.setAttribute("aria-checked", on ? "true" : "false");
    });
    // 축이 없는 상품에서는 체크박스 자체를 숨긴다(마크업이 남아 있어도 조작 대상이 아니다).
    this.root.querySelectorAll("[data-dcent-buy-addon]").forEach((el) => {
      el.hidden = !this.addon;
      el.setAttribute("aria-checked", this.addonOn ? "true" : "false");
    });

    // 수량 카운터 — 표시와 감소 버튼 잠금은 상태의 그림자다. 마크업은 초기값만 내고 이후는 여기서만 쓴다.
    this.root.querySelectorAll("[data-dcent-buy-qty-input]").forEach((el) => {
      if (this.qtyBlank) return; // 사람이 지워 둔 칸은 되채우지 않는다 (보정은 blur)
      const next = String(this.qty);
      if (el.value !== next) el.value = next; // 같은 값 재대입은 캐럿을 튀게 하므로 피한다
    });
    this.root.querySelectorAll("[data-dcent-buy-qty-dec]").forEach((el) => {
      el.toggleAttribute("disabled", this.qty <= 1);
    });

    if (cur) {
      const total = this.buyTotalLabel();
      const fields = {
        // 단가. 뜻을 바꾸지 않는다 — 수량을 곱한 합계는 아래 priceTotal 이다.
        price: cur.price,
        // 히어로 큰 가격(drop-07 포인트 1) — 수량·애드온을 반영한 합계.
        priceTotal: total,
        // 취소선 원가도 수량을 반영한 합계다 — 단가로 두면 수량 2 부터 할인 전 가격이
        // 할인 후보다 작게 찍힌다(MAGE-443). 할인율(discount)은 비율이라 수량과 무관하다.
        compare: this.compareTotalLabel(),
        discount: cur.discount ? `${cur.discount}%` : "",
        label: cur.label,
        badge: cur.badge,
        // 애드온 차액은 선택 **카드** 기준이라 cur(애드온 반영 후 variant)가 아니라 sel 에서 온다
        // — 체크박스를 켜고 끄는 것과 무관하게 "이 카드의 off 대비 on" 으로 같은 값이다.
        // 카드마다 다르므로 전환 때 갱신해야 하는데, 예전엔 Liquid 이 첫 카드 기준으로 한 번
        // 찍고 끝이라 다른 카드로 옮겨도 값이 그대로였다.
        addonDelta: sel && sel.addonDelta ? `+ ${sel.addonDelta}` : "",
        // 바로 구매 버튼 라벨에 들어갈 합계 금액(drop-07 포인트 7 — 금액을 항상 노출한다).
        buyTotal: total,
      };
      document.querySelectorAll("[data-dcent-buy-field]").forEach((el) => {
        const key = el.getAttribute("data-dcent-buy-field");
        if (!(key in fields)) return;
        const value = fields[key];
        if (KEEP_WHEN_EMPTY.has(key)) {
          // 금액이 없으면 서버가 찍은 값을 그대로 둔다(자리를 비우지 않는다 — 위 상수 주석 참조).
          if (!value) return;
          if (key === "buyTotal") {
            const label = (el.dataset.format || "%AMOUNT%").replace(
              "%AMOUNT%",
              value,
            );
            el.textContent = label;
            // 라벨을 접고 아이콘만 남는 축소 상태에서 읽히는 이름이라 같이 따라가야 한다.
            const host = el.closest("[data-dcent-buy-now]");
            if (host) host.setAttribute("aria-label", label);
            return;
          }
          el.textContent = value; // priceTotal — 히어로 큰 가격
          return;
        }
        el.textContent = value;
        el.hidden = !value; // 정가·할인은 세일일 때만 — 빈 값이면 자리를 비운다
      });
      document.querySelectorAll("[data-dcent-buy-image]").forEach((img) => {
        if (cur.image) img.src = cur.image;
        img.toggleAttribute("data-double", Boolean(cur.imageDouble));
      });
    }

    this.renderGauge();
  }

  /**
   * 무료배송 게이지 — 진행분은 담길 실결제가(애드온·수량 반영분). 한국몰은 루트가 없어 자동 비활성.
   *
   * 수량이 들어온 뒤로는 단가가 아니라 buyTotalRaw() 를 본다. 3개를 담아 목표를 이미 넘겼는데
   * "$155 남았습니다" 가 떠 있으면 게이지가 거짓말을 한다.
   */
  renderGauge() {
    const gauge = this.root.querySelector("[data-dcent-ship-goal]");
    if (!gauge) return;
    const goal = Number(gauge.getAttribute("data-dcent-ship-goal")) || 0;
    if (goal <= 0) return;

    const total = this.buyTotalRaw();

    const pct = Math.min(100, (total / goal) * 100);
    const fill = gauge.querySelector("[data-dcent-ship-fill]");
    if (fill) fill.style.width = `${pct}%`;

    const label = gauge.querySelector("[data-dcent-ship-label]");
    if (label)
      label.textContent = `${formatAmount(total, this.currency)} / ${formatAmount(goal, this.currency)}`;

    const text = gauge.querySelector("[data-dcent-ship-text]");
    if (text) {
      const remain = Math.max(0, goal - total);
      text.textContent =
        remain > 0
          ? (text.dataset.remain || "").replace(
              "%AMOUNT%",
              formatAmount(remain, this.currency),
            )
          : text.dataset.unlocked || "";
    }
  }

  /**
   * @param {boolean} [initial] 바인딩 직후의 첫 1회인가.
   *
   * 소비처가 "사람이 옵션을 바꾼 것"과 "화면이 처음 그려진 것"을 구별해야 한다 — 갤러리는
   * 전자에만 따라 움직이고 후자는 무시한다(진입 첫 화면은 씬을 지킨다). 구별이 없으면 초기
   * 1회 이벤트가 곧바로 갤러리를 제품 컷으로 돌려 첫인상이 달라진다.
   */
  /**
   * 상태 브로드캐스트.
   *
   * `reason` 은 **무엇 때문에 상태가 바뀌었나**다. 소비처마다 반응해야 할 이유가 다르기 때문에
   * 싣는다 — 갤러리는 `option` 일 때만 슬라이드를 옮겨야 하고, 고정 바는 어느 이유든 다시 잰다.
   *
   * 이걸 detail 내용으로 추론하게 두면 안 된다. drop-07 에서 수량 축이 생기면서 `+` 를 눌러도
   * 같은 `option.mediaId` 가 실려 나가는데, 받는 쪽이 "옵션이 왔으니 옵션이 바뀐 것" 으로 읽어
   * 진입 직후 수량만 올려도 첫인상 씬이 제품 컷으로 갈아치워졌다. 직전 조합과 비교하는 가드도
   * 세워 봤지만 `initial` 방출을 못 받은 화면에서는 첫 클릭을 그대로 통과시켜 반쪽이었다.
   * 이유를 아는 쪽은 발신부뿐이라 여기서 실어 보낸다.
   */
  emitState(reason = "option") {
    const initial = reason === "init";
    document.dispatchEvent(
      new CustomEvent("dcent:pdp:state", {
        detail: {
          option: this.selected,
          addon: this.addonOn,
          // 고정 바 등 히어로 밖 소비처가 수량을 알아야 할 때 여기서 받는다(갤러리는 무시한다).
          qty: this.qty,
          options: this.options,
          reason,
          initial, // 기존 소비처 호환 — 신규 코드는 reason 을 본다
        },
      }),
    );
  }
}

/**
 * 구매 컬럼 FAQ 를 열고 닫아도 **컬럼 전체 높이가 변하지 않게** 아래 여백을 예약한다 (MAGE-578).
 *
 * 왜 필요한가 — PC(≥1024)에서 갤러리 스테이지는 `position: sticky; height: 100dvh` 이고
 * 구매 컬럼이 `-100dvh` 로 그 위에 겹친다. 그래서 **히어로의 높이 = 컬럼의 높이**이고,
 * 스테이지가 붙어 있을 수 있는 거리도 컬럼 높이가 정한다. FAQ 를 펼치면 컬럼이 그만큼 길어져
 * 스테이지의 릴리스 지점이 아래로 밀리고, 릴리스 구간(히어로 끝자락 — FAQ 가 있는 그 자리다)에
 * 있던 사용자에게는 **왼쪽 제품 사진이 툭 내려가는 것**으로 보인다.
 * 2026-08-30 dev 실측(korea 지문인증형, 1440×900, scrollY 643): 스테이지 top −183.5 → 0.
 *
 * 어떻게 — 답변 중 가장 긴 것의 높이만큼을 늘 확보해 두고, 열린 답변의 높이만큼 되돌려준다.
 * `name` 속성으로 한 번에 하나만 열리므로 (열린 높이 + 예약) 은 항상 같은 값이다.
 * 예약은 상자 **바깥 아래 여백**이라 카드가 커 보이지 않고, FAQ 가 컬럼의 마지막 요소라
 * 눈에 보이는 결과는 다크 히어로가 그만큼 더 이어지는 것뿐이다(다음 섹션도 다크다).
 *
 * 모바일에는 걸지 않는다 — 스테이지가 sticky 가 아니라 흔들릴 것이 없고, 예약은 순수한 빈 공간이
 * 된다. CSS 쪽에서 `@media (min-width: 1024px)` 안에서만 이 변수를 소비한다.
 */
function initBuyFaqReserve(root) {
  const box = root.querySelector(".dcent-buy__faq");
  if (!box) return;
  const rows = Array.from(box.querySelectorAll("details.dcent-buy__faq-row"));
  if (!rows.length) return;

  // 재는 대상은 답변 높이가 아니라 **상자 전체 높이**다. 답변에는 위 여백 12px 이 붙고 행 사이
  // 디바이더도 padding 을 더해, 답변만 재면 열 때마다 12px 이 어긋난다(실측으로 드러났다).
  // 상자를 재면 그런 부수 여백이 전부 포함된다. 예약 여백은 margin 이라 상자 높이에 안 섞인다.
  const boxHeight = () => box.getBoundingClientRect().height;
  let tallest = 0;

  const apply = () => {
    const reserve = Math.max(0, Math.round(tallest - boxHeight()));
    box.style.setProperty("--dbc-faq-reserve", reserve + "px");
  };

  const measure = () => {
    // 닫힌 <details> 의 내용은 렌더되지 않아 높이를 잴 수 없다. 잠깐 열어 재고 원래 상태로 돌린다.
    // 페인트 사이에 끼지 않는 동기 루프라 화면에는 보이지 않는다. `name` 배타 때문에 남의 상태가
    // 함께 바뀌므로, 먼저 전부 기록해 두고 마지막에 통째로 되돌린다.
    const wasOpen = rows.map((row) => row.open);
    tallest = 0;
    rows.forEach((row) => {
      if (!row.querySelector(".dcent-buy__faq-a")) return;
      row.open = true;
      tallest = Math.max(tallest, boxHeight());
    });
    rows.forEach((row, i) => {
      row.open = wasOpen[i];
    });
    apply();
  };

  rows.forEach((row) => row.addEventListener("toggle", apply));

  let resizeTimer = null;
  window.addEventListener("resize", () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measure, 200);
  });

  measure();
}

function init() {
  document.querySelectorAll("[data-dcent-pdp-buy]").forEach((root) => {
    initBuyFaqReserve(root);

    const script = root.querySelector("[data-dcent-buy-context]");
    if (!script) return;
    let context;
    try {
      context = JSON.parse(script.textContent);
    } catch {
      return; // 컨텍스트가 깨지면 링크 기반 폴백이 그대로 남는다
    }
    new DcentPdpBuy(root, context).bind();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
